package com.vamvisions.main.pages.contact

import com.vamvisions.main.theme.NARROW_SCREEN
import com.vamvisions.main.theme.Tokens
import com.vamvisions.main.theme.cssVar
import org.jetbrains.compose.web.css.StyleSheet
import org.jetbrains.compose.web.css.media

/** Viewports narrower than 900 CSS pixels, where the Contact page's two columns stack. */
private const val CONTACT_SINGLE_COLUMN = "(max-width: 899.98px)"

/** The smallest tap target, in both directions. */
private const val MIN_TAP_SIZE = "44px"

/**
 * The Contact page: intro, then the photo and the "Reach me directly" card beside the form card, on glass over the
 * background. Sizes follow the Figma "Contact" frames at an 18px root.
 */
object ContactStyles : StyleSheet() {

    val page by style {
        property("display", "flex")
        property("flex-direction", "column")
        property("gap", "2.67rem")
    }

    val intro by style {
        property("display", "flex")
        property("flex-direction", "column")
        property("align-items", "center")
        property("gap", "0.67rem")
        property("text-align", "center")
    }

    val introTitle by style {
        property("margin", "0")
        property("font-size", "clamp(1.8rem, 6vw, 2.6rem)")
        property("line-height", "1.2")
    }

    val introText by style {
        property("max-width", "43.33rem")
        property("margin", "0")
        property("font-size", "1.2rem")
        media(NARROW_SCREEN) {
            self style {
                property("font-size", "1rem")
            }
        }
    }

    /**
     * The photo with the "Reach me directly" card under it, beside the form card. In one column, the DOM order (photo,
     * form, links) is also the reading order.
     */
    val grid by style {
        property("display", "grid")
        property("grid-template-columns", "21.11rem minmax(0, 1fr)")
        property("grid-template-rows", "auto 1fr")
        property("grid-template-areas", "\"photo form\" \"reach form\"")
        property("gap", "1.11rem 1.78rem")
        property("align-items", "start")
        media(CONTACT_SINGLE_COLUMN) {
            self style {
                property("grid-template-columns", "minmax(0, 1fr)")
                property("grid-template-rows", "auto")
                property("grid-template-areas", "\"photo\" \"form\" \"reach\"")
            }
        }
    }

    /** Holds the photo, which fills it. On narrow screens it is a banner. The crop stays on the owner's face. */
    val photoFrame by style {
        property("grid-area", "photo")
        property("position", "relative")
        property("height", "23.17rem")
        property("overflow", "hidden")
        property("border", "4px solid ${cssVar(Tokens.COLOR_GLASS_STROKE)}")
        property("border-radius", "1rem")
        property("box-shadow", cssVar(Tokens.SHADOW_GLASS))
        media(CONTACT_SINGLE_COLUMN) {
            self style {
                property("height", "auto")
                property("aspect-ratio", "6 / 5")
                property("max-height", "24rem")
            }
        }
    }

    val photo by style {
        property("position", "absolute")
        property("inset", "0")
        property("width", "100%")
        property("height", "100%")
        property("object-fit", "cover")
        property("object-position", "78% 35%")
    }

    val formCard by style {
        property("grid-area", "form")
        property("display", "flex")
        property("flex-direction", "column")
        property("gap", "1.22rem")
        property("padding", "1.78rem")
        media(NARROW_SCREEN) {
            self style {
                property("padding", "1.33rem 1rem")
            }
        }
    }

    val reachCard by style {
        property("grid-area", "reach")
        property("display", "flex")
        property("flex-direction", "column")
        property("gap", "0.78rem")
        property("padding", "1.33rem")
    }

    val cardTitle by style {
        property("margin", "0")
        property("font-size", "1.56rem")
        property("line-height", "1.25")
    }

    val reachTitle by style {
        property("margin", "0")
        property("font-size", "1.22rem")
        property("line-height", "1.25")
    }

    val pills by style {
        property("display", "flex")
        property("flex-wrap", "wrap")
        property("gap", "0.44rem")
        // The email address is long; on the narrowest phones it may wrap rather than overflow.
        property("overflow-wrap", "anywhere")
    }

    val form by style {
        property("display", "flex")
        property("flex-direction", "column")
        property("gap", "1.22rem")
        property("position", "relative")
    }

    // Topic chips

    val topic by style {
        property("display", "flex")
        property("flex-direction", "column")
        property("gap", "0.56rem")
    }

    val optionalHint by style {
        property("font-weight", "400")
        property("color", cssVar(Tokens.COLOR_MUTED))
    }

    val chips by style {
        property("display", "flex")
        property("flex-wrap", "wrap")
        property("gap", "0.44rem")
    }

    /** A topic: an outline pill, filled with the brand colour and a check mark while checked. */
    val chip by style {
        property("position", "relative")
        property("display", "inline-flex")
        property("align-items", "center")
        property("gap", "0.33rem")
        property("min-height", "2.22rem")
        property("padding", "0 0.89rem")
        property("font", "inherit")
        property("font-size", "0.78rem")
        property("font-weight", "600")
        property("color", cssVar(Tokens.COLOR_ON_BG))
        property("background-color", "transparent")
        property("border", "1px solid ${cssVar(Tokens.COLOR_MUTED)}")
        property("border-radius", "999px")
        property("cursor", "pointer")
        property("transition", "background-color 0.2s")
        self + ":hover" style {
            property("background-color", cssVar(Tokens.COLOR_CHIP))
        }
        self + "[aria-checked=true]" style {
            property("color", cssVar(Tokens.COLOR_ON_BRAND))
            property("background-color", cssVar(Tokens.COLOR_BRAND))
            property("border-color", cssVar(Tokens.COLOR_BRAND))
        }
        // The tap area reaches 44 CSS pixels tall, above and below the chip.
        self + "::before" style {
            property("content", "\"\"")
            property("position", "absolute")
            property("inset", "min(0px, calc((100% - $MIN_TAP_SIZE) / 2)) 0")
        }
    }

    val chipCheck by style {
        property("font-weight", "700")
    }

    // Fields

    /** Name and email side by side; stacked on phones. */
    val fieldRow by style {
        property("display", "grid")
        property("grid-template-columns", "repeat(2, minmax(0, 1fr))")
        property("gap", "1.22rem 0.89rem")
        media(NARROW_SCREEN) {
            self style {
                property("grid-template-columns", "minmax(0, 1fr)")
            }
        }
    }

    val field by style {
        property("display", "flex")
        property("flex-direction", "column")
        property("gap", "0.33rem")
        property("min-width", "0")
    }

    /** A field's label, and the topic question above the chips. */
    val fieldLabel by style {
        property("margin", "0")
        property("font-size", "0.83rem")
        property("font-weight", "600")
    }

    /** Text inputs and the message text area: a rounded box, outlined in the error colour while invalid. */
    val textInput by style {
        property("width", "100%")
        property("min-height", "2.78rem")
        property("padding", "0 0.89rem")
        property("font", "inherit")
        property("font-size", "0.89rem")
        property("color", cssVar(Tokens.COLOR_ON_BG))
        property("background-color", cssVar(Tokens.COLOR_SURFACE))
        property("border", "1px solid ${cssVar(Tokens.COLOR_MUTED)}")
        property("border-radius", "0.67rem")
        self + "::placeholder" style {
            // Opaque, so placeholders keep the muted colour's 4.5:1 contrast.
            property("color", cssVar(Tokens.COLOR_MUTED))
            property("opacity", "1")
        }
        self + "[aria-invalid=true]" style {
            property("border-color", cssVar(Tokens.COLOR_ERROR))
        }
    }

    /** A fixed height; longer text scrolls inside it. */
    val textArea by style {
        property("height", "8.89rem")
        property("padding-block", "0.78rem")
        property("line-height", "1.5")
        property("resize", "none")
        property("overflow-y", "auto")
    }

    val fieldError by style {
        property("margin", "0")
        property("font-size", "0.78rem")
        property("font-weight", "600")
        property("color", cssVar(Tokens.COLOR_ERROR))
    }

    // Send and result

    /** The filled main action, as the Figma Button / Primary. Full width on phones. */
    val sendButton by style {
        property("display", "inline-flex")
        property("align-self", "flex-start")
        property("align-items", "center")
        property("justify-content", "center")
        property("gap", "0.55rem")
        property("min-width", "11.11rem")
        property("min-height", "2.78rem")
        property("padding", "0 2rem")
        property("font", "inherit")
        property("font-size", "0.89rem")
        property("font-weight", "600")
        property("color", cssVar(Tokens.COLOR_ON_BRAND))
        property("background-color", cssVar(Tokens.COLOR_BRAND))
        property("border", "none")
        property("border-radius", "999px")
        property("box-shadow", "0 6px 18px color-mix(in srgb, ${cssVar(Tokens.COLOR_BRAND)} 25%, transparent)")
        property("cursor", "pointer")
        property("transition", "filter 0.2s")
        self + ":hover" style {
            property("filter", "brightness(1.12)")
        }
        self + ":disabled" style {
            property("cursor", "not-allowed")
            property("opacity", "0.6")
            property("filter", "none")
        }
        // The arrow after the label.
        desc(self, "span") style {
            property("font-size", "1rem")
        }
        media(NARROW_SCREEN) {
            self style {
                property("align-self", "stretch")
            }
        }
    }

    /** The `role="status"` line, always present so the result is announced when it appears. */
    val result by style {
        property("margin", "0")
        property("font-size", "0.89rem")
        property("font-weight", "600")
        property("line-height", "1.5")
        // Empty, it takes no room, so the form card ends at the button. It stays in the accessibility tree.
        self + ":empty" style {
            property("margin-top", "-1.22rem")
        }
    }

    val resultSuccess by style {
        property("color", cssVar(Tokens.COLOR_BRAND))
    }

    val resultFailure by style {
        property("color", cssVar(Tokens.COLOR_ERROR))
    }

    /**
     * Moves the honeypot field off-screen. It stays in the form for bots but is hidden from people, keyboard focus
     * and assistive technology.
     */
    val honeypot by style {
        property("position", "absolute")
        property("left", "-10000px")
        property("width", "1px")
        property("height", "1px")
        property("overflow", "hidden")
    }
}
